import { SystemRole } from '@planning/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShellApp, type EnlaceProps, type SeccionNavegacion } from '@xiltrion/ui/bloques/shell-app';
import { CentroNotificaciones } from '@xiltrion/ui/bloques/centro-notificaciones';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { EstadoCargando, EstadoPendiente } from '@xiltrion/ui/bloques/estados';
import { Alert, AlertAction, AlertDescription } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Kbd } from '@xiltrion/ui/kbd';
import { apiGet, apiPost } from '@/api/client';
import { useProjects } from '@/api/hooks';
import type { NotificationRow } from '@/api/types';
import { registry } from './extension-points';
import { useSession, useTerms } from './session';
import { setThemeMode, getThemeMode } from './theme';
import { useRealtime } from './realtime';
import { SearchPalette } from '@/core/search/SearchPalette';
import { setupGate } from './setup-gate';

const Enlace = ({ href, className, children, onClick, ...rest }: EnlaceProps) => <Link to={href} className={className} onClick={onClick} aria-current={rest['aria-current']}>{children}</Link>;

/**
 * Armazón de Planning sobre `ShellApp` (spec §7). En puesta en marcha (como en SGDEA, `setupGate`) quien la hace solo
 * tiene el asistente, sin barra lateral, y los demás ven que la organización se prepara.
 */
export function AppShell() {
  const { me, loading, can } = useSession();
  const location = useLocation();
  if (loading) return <div className="p-10"><EstadoCargando /></div>;
  if (!me) return <Navigate to={`/ingreso?volver=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  const gate = setupGate(me, can('admin.advanced'), location.pathname);
  if (gate === 'preparing') return <Preparando />;
  if (gate !== 'app') return <Navigate to={gate.redirect} replace />;
  return me.operation === 'SETUP' ? <ShellPuestaEnMarcha /> : <ShellOperacion />;
}

const temaItem = () => ({ etiqueta: getThemeMode() === 'dark' ? 'Modo claro' : 'Modo oscuro', icono: 'mdi:theme-light-dark' as const, onSelect: () => setThemeMode(getThemeMode() === 'dark' ? 'light' : 'dark') });
const salirItem = (onSelect: () => void) => ({ etiqueta: 'Cerrar sesión', icono: 'mdi:logout' as const, destructivo: true, onSelect });

/**
 * Cierre de sesión único de la suite (GUIA §3.1 «Salir»): el mismo texto en todas las apps. `logout` va al cierre de
 * la suite; si falla, avisa y rechaza, y el diálogo queda abierto.
 */
function SalirDialogo({ abierto, onAbiertoChange }: { abierto: boolean; onAbiertoChange: (v: boolean) => void }) {
  const { logout } = useSession();
  return <ConfirmarDialogo abierto={abierto} onAbiertoChange={onAbiertoChange} titulo="¿Está seguro que desea cerrar su sesión?" descripcion="Se cerrará en todas las apps de la suite Datalog." textoConfirmar="Cerrar sesión" tamano="sm" tono="warning" icono="mdi:logout" onConfirmar={logout} />;
}

const franjaMfa = (onActivar: () => void) => (
  <Alert variant="warning" className="rounded-none border-x-0 border-t-0"><Icono nombre="mdi:alert-outline" /><AlertDescription>Activa la verificación en dos pasos para poder hacer cambios.</AlertDescription><AlertAction><Button size="sm" onClick={onActivar}>Activar</Button></AlertAction></Alert>
);

/** Toda la vista de quien no hace la puesta en marcha: sin menú, solo «Cerrar sesión». Al pasar a operación, `/me` cambia y la app aparece sola. */
function Preparando() {
  const { me } = useSession();
  const [leaving, setLeaving] = useState(false);
  return (
    <div className="grid min-h-svh place-items-center bg-background p-6">
      <EstadoPendiente
        titulo="Tu organización se está preparando"
        descripcion={`${me?.tenant.name || 'Tu organización'} todavía está en la puesta en marcha de Planning. Cuando el administrador la termine podrás entrar a trabajar aquí; no tienes que hacer nada mientras tanto.`}
        accion={<Button variant="outline" onClick={() => setLeaving(true)}><Icono nombre="mdi:logout" aria-hidden /> Cerrar sesión</Button>}
      />
      <SalirDialogo abierto={leaving} onAbiertoChange={setLeaving} />
    </div>
  );
}

/** Puesta en marcha: sin barra lateral, selector de apps, búsqueda ni avisos; en el menú, solo el tema y «Cerrar sesión». */
function ShellPuestaEnMarcha() {
  const { me } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  if (!me) return null;
  return (
    <ShellApp
      nombreApp="Planning"
      navegacion={[]}
      rutaActual={location.pathname}
      Enlace={Enlace}
      sinBarraLateral
      entidad={{ nombre: me.tenant.name, logo: me.branding?.logoUrl ?? undefined }}
      usuario={{ nombre: me.displayName, correo: me.email, color: me.avatarColor }}
      // Con ingreso local y la verificación en dos pasos exigida no se puede cambiar nada sin activarla («Mi perfil»).
      franja={me.mfaSetupRequired && location.pathname !== '/perfil' ? franjaMfa(() => navigate('/perfil')) : null}
      vistaSuperadmin={me.superadmin}
      version={__APP_VERSION__}
      menuUsuario={[temaItem(), salirItem(() => setLeaving(true))]}
    >
      <Outlet />
      <SalirDialogo abierto={leaving} onAbiertoChange={setLeaving} />
    </ShellApp>
  );
}

/** En operación: navegación del árbol funcional filtrada por módulos y permisos. */
function ShellOperacion() {
  const { me, has, can, terms } = useSession();
  const { t } = useTerms();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const { data: projects = [] } = useProjects();
  const { data: notifications = [] } = useQuery({ queryKey: ['notifications', 'recent'], queryFn: () => apiGet<NotificationRow[]>('/notifications'), enabled: !!me, refetchInterval: 120_000 });
  const { data: pendingCount } = useQuery({ queryKey: ['decisions', 'count'], queryFn: async () => (await apiGet<unknown[]>('/decisions/pending')).length + (await apiGet<unknown[]>('/intake')).length, enabled: !!me, refetchInterval: 120_000 });
  useRealtime([]);
  // ⌘K abre la búsqueda; el botón va en la barra superior, en el mismo sitio que en SGDEA (lineamiento común de las apps).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearch(true); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  if (!me) return null;

  const label = (l: string | { term: keyof typeof terms; plural?: boolean }) => (typeof l === 'string' ? l : t(l.term, l.plural));
  const extra = (section: 'work' | 'management' | 'admin') => registry.navItems().filter((n) => n.section === section && has(n.module) && (!n.permissions || n.permissions.some((p) => can(p as never)))).map((n) => ({ id: n.id, etiqueta: label(n.label), icono: n.icon, href: n.href }));
  const unread = notifications.filter((n) => !n.readAt).length;
  const management = [
    ...(has('core.goals') ? [{ id: 'goals', etiqueta: t('goal', true), icono: 'mdi:target' as const, href: '/objetivos' }] : []),
    ...(has('core.portfolios') ? [{ id: 'portfolios', etiqueta: t('portfolio', true), icono: 'mdi:briefcase-outline' as const, href: '/portafolios' }] : []),
    ...(has('core.external') ? [{ id: 'external', etiqueta: t('external_collaborator', true), icono: 'mdi:account-hard-hat-outline' as const, href: '/colaboradores' }] : []),
    ...(has('core.corrective') ? [{ id: 'corrective', etiqueta: t('corrective_plan', true), icono: 'mdi:clipboard-check-outline' as const, href: '/acciones-correctivas' }] : []),
    ...(has('core.risks') ? [{ id: 'risks', etiqueta: t('risk', true), icono: 'mdi:shield-alert-outline' as const, href: '/riesgos' }] : []),
    ...(has('core.meetings') ? [{ id: 'meetings', etiqueta: t('meeting', true), icono: 'mdi:account-group-outline' as const, href: '/reuniones' }] : []),
    ...extra('management'),
    ...(can('reports.read') ? [{ id: 'reports', etiqueta: 'Reportes', icono: 'mdi:chart-bar' as const, href: '/reportes' }] : []),
  ];
  const navegacion: SeccionNavegacion[] = [
    {
      id: 'work',
      items: [
        { id: 'home', etiqueta: 'Inicio', icono: 'mdi:home-outline', href: '/' },
        { id: 'mine', etiqueta: 'Mis tareas', icono: 'mdi:account-check-outline', href: '/mis-tareas' },
        { id: 'inbox', etiqueta: 'Bandeja', icono: 'mdi:inbox-arrow-down-outline', href: '/bandeja', insignia: pendingCount ? <Badge size="sm" variant="primary-light" className="tnum">{pendingCount}</Badge> : undefined },
        ...extra('work'),
      ],
    },
    {
      id: 'projects',
      titulo: t('project', true),
      accion: can('project.create') ? <Link to="/proyectos?nuevo=1" className="rounded p-0.5 hover:bg-sidebar-accent" aria-label="Nuevo proyecto"><Icono nombre="mdi:plus" className="size-3.5" /></Link> : undefined,
      items: [{ id: 'all', etiqueta: `Todos los ${t('project', true).toLowerCase()}`, icono: 'mdi:folder-outline', href: '/proyectos' }, ...projects.slice(0, 15).map((p) => ({ id: p.id, etiqueta: p.name, color: p.color, href: `/proyectos/${p.id}` }))],
    },
    // Gestión como grupo plegable (ítem con `hijos`): cerrado al entrar, abierto si la ruta actual es una de sus entradas.
    ...(management.length ? [{ id: 'management', items: [{ id: 'management', etiqueta: 'Gestión', icono: 'mdi:view-dashboard-outline' as const, hijos: management }] }] : []),
    ...(can('admin.manage') || can('audit.read') || can('admin.advanced')
      ? [{
          id: 'admin',
          titulo: 'Administración',
          items: [
            ...(can('admin.manage') ? [{ id: 'users', etiqueta: 'Personas', icono: 'mdi:account-group-outline' as const, href: '/admin/personas' }, { id: 'teams', etiqueta: t('team', true), icono: 'mdi:sitemap-outline' as const, href: '/admin/equipos' }] : []),
            ...(can('admin.advanced') ? [{ id: 'engine', etiqueta: 'Motor de decisión', icono: 'mdi:robot-outline' as const, href: '/admin/motor' }] : []),
            ...(can('admin.manage') ? [{ id: 'settings', etiqueta: 'Configuración', icono: 'mdi:cog-outline' as const, href: '/admin/configuracion' }] : []),
            ...(can('audit.read') ? [{ id: 'audit', etiqueta: 'Auditoría', icono: 'mdi:history' as const, href: '/admin/auditoria' }] : []),
            ...extra('admin'),
          ],
        }]
      : []),
  ];
  const franja: ReactNode = me.mfaSetupRequired ? franjaMfa(() => navigate('/perfil')) : null;
  return (
    <ShellApp
      nombreApp="Planning"
      navegacion={navegacion}
      rutaActual={location.pathname}
      Enlace={Enlace}
      apps={me.suiteApps}
      // «Panel de control» al pie del lanzador: la entidad en la consola de la suite, solo para el Administrador (el rol
      // que la suite alinea con el ADMIN de la entidad) y con la suite conectada.
      panelControl={me.suite.url && me.roleCodes.includes(SystemRole.ADMIN) ? me.suite.url : undefined}
      entidad={{ nombre: me.tenant.name, logo: me.branding?.logoUrl ?? undefined }}
      usuario={{ nombre: me.displayName, correo: me.email, color: me.avatarColor }}
      barraSuperior={
        <Button variant="outline" size="sm" onClick={() => setSearch(true)} className="w-72 max-w-full justify-start gap-2 px-3 font-normal text-muted-foreground" aria-label="Buscar (atajo ⌘K)">
          <Icono nombre="mdi:magnify" aria-hidden /> <span className="truncate">Buscar tareas, proyectos, personas…</span> <Kbd className="ml-auto">⌘K</Kbd>
        </Button>
      }
      franja={franja}
      vistaSuperadmin={me.superadmin}
      avisos={<CentroNotificaciones noLeidos={unread} avisos={notifications.slice(0, 20).map((n) => ({ id: n.id, titulo: n.title, detalle: n.body, fecha: n.createdAt, leido: !!n.readAt }))} onAbrir={(a) => { const n = notifications.find((x) => x.id === a.id); if (n?.link) navigate(n.link); }} onMarcarLeidos={(ids) => void apiPost('/notifications/read', { ids: ids === 'todos' ? 'all' : ids }).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }))} onVerTodos={() => navigate('/avisos')} />}
      version={__APP_VERSION__}
      menuUsuario={[
        { etiqueta: 'Mi perfil', icono: 'mdi:account-circle-outline', onSelect: () => navigate('/perfil') },
        temaItem(),
        salirItem(() => setLeaving(true)),
      ]}
    >
      <Outlet />
      <SearchPalette open={search} onOpenChange={setSearch} />
      <SalirDialogo abierto={leaving} onAbiertoChange={setLeaving} />
    </ShellApp>
  );
}
